import {
  Addon,
  Flex,
  IconButton,
  Image,
  List,
  Menu,
  Typography,
  useToast,
} from "@smartfaster-ui/components";
import { vars } from "@smartfaster-ui/theming/css/theme.contract.css";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useInteriorDesigner } from "../context/InteriorDesignerContext";
import { getActiveNodesSorted } from "../context/selectors";
import type { TreeNode } from "../context/types";
import { useHistoryActions } from "../hooks/useHistoryActions";
import { getDescendantIds } from "../utils/getDescendantIds";
import { getNodeSummary } from "../utils/getNodeSummary";
import { groupTreeNodesByDate } from "../utils/groupByDate";
import { Panel } from "./Panel";

export function HistoryPanel() {
  const { t, i18n } = useTranslation();
  const { state, dispatch } = useInteriorDesigner();
  const { softDelete, navigateToNode } = useHistoryActions();
  const toast = useToast();

  const [deletingId, setDeletingId] = useState<string | null>(null);

  const groups = groupTreeNodesByDate({
    nodes: state.tree,
    locale: i18n.language,
    t,
  });

  const handleNavigate = ({ node }: { node: TreeNode }) => {
    if (node.id === state.activeNodeId) return;
    dispatch({ type: "NODE_NAVIGATED", nodeId: node.id });
    navigateToNode({ photoId: node.id }).catch(() => {});
  };

  const handleDelete = async ({ node }: { node: TreeNode }) => {
    setDeletingId(node.id);
    try {
      const affectedIds = getDescendantIds({
        nodeId: node.id,
        tree: state.tree,
      });
      if (affectedIds.has(state.activeNodeId)) {
        const sorted = getActiveNodesSorted({ tree: state.tree });
        const deletedIndex = sorted.findIndex((n) => n.id === node.id);

        const previous = sorted
          .slice(0, deletedIndex)
          .findLast((n) => !affectedIds.has(n.id));
        const next = sorted
          .slice(deletedIndex + 1)
          .find((n) => !affectedIds.has(n.id));

        const fallbackId = (previous ?? next)?.id ?? state.rootPhotoId;
        dispatch({ type: "NODE_NAVIGATED", nodeId: fallbackId });
        navigateToNode({ photoId: fallbackId }).catch(() => {});
      }

      const { deletedAt, fallsBackTo } = await softDelete({
        photoId: node.id,
      });
      dispatch({
        type: "NODE_SOFT_DELETED",
        nodeId: node.id,
        fallsBackTo,
        deletedAt,
      });
      toast.show({
        title: t("interiorDesigner.history.deleteSuccess"),
        variant: "success",
      });
    } catch (error: unknown) {
      const resp = (
        error as { response?: { status?: number; data?: { detail?: string } } }
      )?.response;
      if (
        resp?.status === 409 &&
        resp.data?.detail?.includes("already deleted")
      ) {
        dispatch({
          type: "NODE_SOFT_DELETED",
          nodeId: node.id,
          fallsBackTo: null,
          deletedAt: new Date().toISOString(),
        });
      } else if (resp?.status === 409) {
        toast.show({
          title: t("interiorDesigner.history.deleteActiveError"),
          variant: "warning",
        });
      } else {
        toast.show({
          title: t("interiorDesigner.history.deleteError"),
          variant: "error",
        });
      }
    } finally {
      setDeletingId(null);
    }
  };

  const isRoot = (node: TreeNode) => node.parent_photo_id === null;
  const isActive = (node: TreeNode) => node.id === state.activeNodeId;

  return (
    <Panel requiresVocab={false}>
      <Flex
        direction="column"
        gap="sm"
        padding="sm"
        background="surfaceNeutralSecondary"
        radius="lg"
      >
        {groups.map((group) => (
          <Flex key={group.label} direction="column" gap="sm">
            <Typography variant="bodyMd" weight="bold">
              {group.label}
            </Typography>

            <List variant="separated">
              {group.nodes.map((node) => {
                const { title, subtitle } = getNodeSummary({ node, t });
                const isDeleting = deletingId === node.id;

                return (
                  <List.Row
                    key={node.id}
                    onClick={() => handleNavigate({ node })}
                    style={{
                      cursor: "pointer",
                      paddingInlineStart: vars.spacing.xs,
                      paddingInlineEnd: vars.spacing.sm,
                      borderRadius: vars.radius.md,
                      ...(isActive(node)
                        ? { borderColor: vars.color.primary }
                        : {}),
                    }}
                    leading={
                      <Image
                        src={node.thumbnail_url}
                        alt={title}
                        radius="xs"
                        fit="cover"
                        style={{ width: 48, height: 48 }}
                      />
                    }
                    trailing={
                      !isRoot(node) ? (
                        <Flex
                          align="center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Menu
                            options={[
                              {
                                label: t(
                                  "interiorDesigner.history.deleteLabel",
                                ),
                                leading: <Addon icon="Delete02Icon" />,
                                variant: "error",
                                disabled: isDeleting,
                                onClick: () => handleDelete({ node }),
                              },
                            ]}
                            side="bottom"
                            align="end"
                          >
                            <IconButton
                              icon="MoreVerticalIcon"
                              variant="ghost"
                              size="sm"
                              color="surfaceNeutralSecondary"
                            />
                          </Menu>
                        </Flex>
                      ) : undefined
                    }
                  >
                    <Flex direction="column">
                      <Typography variant="bodySm" weight="semibold">
                        {title}
                      </Typography>
                      {subtitle && (
                        <Typography
                          variant="bodyXs"
                          color="foregroundTertiary"
                          italic
                        >
                          {subtitle}
                        </Typography>
                      )}
                    </Flex>
                  </List.Row>
                );
              })}
            </List>
          </Flex>
        ))}
      </Flex>
    </Panel>
  );
}
